import { supabase } from "@cl/supabase";
import type {
  ContactStatus,
  Priority,
} from "@u/crmVocab";

// `stage` is the DERIVED pipeline-position rollup (from the contact's leads +
// deals), NOT the relationship. The relationship axis is `status` (crmVocab).
export const CONTACT_STAGES = [
  "new",
  "contacted",
  "qualified",
  "won",
  "lost",
] as const;
export type ContactStage = (typeof CONTACT_STAGES)[number];

export interface Contact {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  preferred_language: string | null;
  tags: string[];
  /** Derived pipeline position (rollup of leads + deals). Read-only. */
  stage: ContactStage;
  /** Relationship with the person (stored, editable). */
  status: ContactStatus;
  /** How urgent this contact is (stored, editable). */
  priority: Priority;
  /** 0-100 lead score. Not computed yet (Fase D hook). */
  score: number;
  notes: string | null;
  assigned_to: string | null;
  /** Property (360 tour) this contact is interested in. Only the detail read
   *  (`fetchContact` / v_crm_contacts) populates it; the list RPC omits it. */
  interested_tour_id?: string | null;
  first_seen_at: string;
  last_activity_at: string;
  created_at: string;
  /** Products that originated this contact ('360'); empty = manual. */
  products: string[];
}

export type ContactTimelineKind =
  | "note"
  | "status"
  | "lead"
  | "booking"
  | "wa_message"
  | "email"
  | "task";

export interface ContactTimelineItem {
  id: string;
  contact_id: string;
  kind: ContactTimelineKind;
  body: string | null;
  meta: Record<string, unknown> | null;
  created_by: string | null;
  created_at: string;
}

// Scoped to the owner in scope (always the signed-in user since team seats were
// removed 2026-09-14).
export async function fetchContacts(ownerId: string): Promise<Contact[]> {
  // Product-scoped server-side: only contacts reachable through a product whose
  // plan pays for CRM (is_demo/admin get all + products[] for client preview).
  const { data, error } = await supabase.rpc("fetch_crm_contacts", {
    p_owner: ownerId,
  });
  if (error) throw error;
  return ((data ?? []) as Contact[]).map((c) => ({
    ...c,
    products: c.products ?? [],
  }));
}

export async function fetchContact(id: string): Promise<Contact | null> {
  // v_crm_contacts adds the derived `stage` roll-up (from the contact's leads +
  // deals); the base `contacts` table no longer stores a stage of its own.
  const { data, error } = await supabase
    .from("v_crm_contacts")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  // v_crm_contacts derives products[] the same way the list RPC does.
  return data
    ? ({ ...(data as Contact), products: (data as Contact).products ?? [] })
    : null;
}

/** Just the score of one contact (RLS owner-scoped). Feeds the ScoreChip on a
 *  lead detail without pulling the whole contact row. */
export async function fetchContactScore(id: string): Promise<number> {
  const { data, error } = await supabase
    .from("contacts")
    .select("score")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return (data?.score as number | undefined) ?? 0;
}

/** Batch scores for a set of contacts (RLS owner-scoped). Only rows with a
 *  score > 0 come back — feeds the list-row chips without N queries. */
export async function fetchContactScores(
  ids: string[],
): Promise<Record<string, number>> {
  if (ids.length === 0) return {};
  const { data, error } = await supabase
    .from("contacts")
    .select("id, score")
    .in("id", ids)
    .gt("score", 0);
  if (error) throw error;
  return Object.fromEntries(
    ((data ?? []) as { id: string; score: number }[]).map((c) => [
      c.id,
      c.score,
    ]),
  );
}

export async function fetchContactTimeline(
  id: string,
): Promise<ContactTimelineItem[]> {
  const { data, error } = await supabase
    .from("contact_activity")
    .select("*")
    .eq("contact_id", id)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as ContactTimelineItem[];
}

export interface ContactPatch {
  name?: string | null;
  // No `stage` — a contact's stage is derived from its leads/deals (read-only).
  // `status`/`priority` ARE the editable relationship axes.
  status?: ContactStatus;
  priority?: Priority;
  notes?: string | null;
  tags?: string[];
  assigned_to?: string | null;
  /** Property (360 tour) of interest, or null to clear. */
  interested_tour_id?: string | null;
}

export async function updateContact(
  id: string,
  patch: ContactPatch,
): Promise<void> {
  const { error } = await supabase.from("contacts").update(patch).eq("id", id);
  if (error) throw error;
}

export async function addContactNote(id: string, body: string): Promise<void> {
  return logContactActivity(id, "note", body);
}

/** Append any activity kind to a contact's timeline (created_by defaults to the
 *  current user via the column default). Used for notes + logged comms. */
export async function logContactActivity(
  id: string,
  kind: ContactTimelineKind,
  body?: string | null,
): Promise<void> {
  const clean = body?.trim() || null;
  const { error } = await supabase
    .from("contact_activity")
    .insert({ contact_id: id, kind, body: clean });
  if (error) throw error;
}

/** Re-point a duplicate's leads/activity/deals onto the kept contact, then
 *  delete it. Backed by the owner-gated merge_contacts RPC (Phase 2). */
export async function mergeContacts(
  keepId: string,
  dropId: string,
): Promise<void> {
  const { error } = await supabase.rpc("merge_contacts", {
    p_keep: keepId,
    p_drop: dropId,
  });
  if (error) throw error;
}

/** Send a one-off email to a contact via the send-crm-email edge function.
 *  The fn resolves the recipient + owner reply-to server-side and logs an
 *  `email` activity on success. */
export async function sendContactEmail(
  contactId: string,
  subject: string,
  body: string,
): Promise<void> {
  const { error } = await supabase.functions.invoke("send-crm-email", {
    body: { contact_id: contactId, subject, body },
  });
  if (error) throw error;
}

/** Import one contact into `ownerId`'s account through the dedupe resolver
 *  (the RPC enforces caller = owner). */
export async function importContact(
  ownerId: string,
  row: {
    name?: string;
    email?: string;
    phone?: string;
  },
): Promise<void> {
  const { error } = await supabase.rpc("crm_import_contact", {
    p_owner: ownerId,
    p_name: row.name?.trim() || null,
    p_email: row.email?.trim() || null,
    p_phone: row.phone?.trim() || null,
  });
  if (error) throw error;
}

export interface MergeCandidate {
  id: string;
  contact_a: string;
  contact_b: string;
  reason: string;
  created_at: string;
}

/** Unresolved duplicate pairs the resolver flagged for the active account. */
export async function fetchMergeCandidates(
  ownerId: string,
): Promise<MergeCandidate[]> {
  const { data, error } = await supabase
    .from("contact_merge_candidates")
    .select("id, contact_a, contact_b, reason, created_at")
    .eq("owner_id", ownerId)
    .is("resolved_at", null)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as MergeCandidate[];
}

/** Contactos que consultaron por un tour concreto. El vínculo vive en
 *  `tour_leads.contact_id`; RLS ya scopea las leads al dueño del tour. Alimenta
 *  el modo embebido de /contacts dentro del editor, que reemplazó a la lista de
 *  leads por tour. */
export async function fetchContactIdsByTour(tourId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("tour_leads")
    .select("contact_id")
    .eq("tour_id", tourId);
  if (error) throw error;
  const ids = (data ?? [])
    .map((r) => (r as { contact_id: string | null }).contact_id)
    .filter((id): id is string => !!id);
  return [...new Set(ids)];
}
